import { useContext, useEffect, useRef } from "react";
import { AuthContext } from "@/context/AuthContext";
import useCurrentUserQuery from "@/api/query/useCurrentUserQuery";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getCurrentOrgRole, getCurrentUserOrganisation } from "@/utils/roleUtils";

export function useUser() {
  const { i18n } = useTranslation();
  const { setAuthUser, authUser } = useContext(AuthContext);
  const fetchingUser = useRef(false);
  const navigate = useNavigate();

  const { data, refetch, isFetching, isFetched, isError } =
    useCurrentUserQuery();

  // Trigger fetch if user is not in state
  useEffect(() => {
    if (!authUser) {
      refetch();
    }
  }, [authUser, refetch]);

  useEffect(() => {
    if (isFetching) {
      fetchingUser.current = true;
    }

    if (isFetched && !isFetching && fetchingUser.current) {
      fetchingUser.current = false;
      const user = data?.data?.data ? data.data.data : null;
      setAuthUser(user);

      if (user) {
        // Handle successful authentication
        i18n.changeLanguage(user.language?.toLowerCase());
        localStorage.setItem(
          "organisation-id",
          user.userOrganisation[0]?.organisationId
        );

        if (!user.isVerified && !window.location.pathname.includes("collaborator")) {
          navigate("/verify-email");
        }
      } else {
        // Redirect to login if /me API call fails (e.g. 500 or any error) after retries
        const pathname = window.location.pathname;
        const isAuthPage =
          pathname.includes("/login") ||
          pathname.includes("/signup") ||
          pathname.includes("/forgot-password") ||
          pathname.includes("/reset-password") ||
          pathname.startsWith("/docs") ||
          pathname.includes("/term-and-condition") ||
          pathname.includes("/privacy-policy");

        if (isError && !isAuthPage) {
          navigate("/login");
        }
      }
    }
  }, [
    isFetching,
    isFetched,
    data,
    isError,
    setAuthUser,
    i18n,
    navigate
  ]);

  const organisationId = authUser?.userOrganisation?.length ? authUser.userOrganisation[0]?.organisationId : "";
  const orgRole = getCurrentOrgRole(authUser, organisationId) ?? null;
  const currentOrg = getCurrentUserOrganisation(authUser, organisationId);

  return { user: authUser, organisationId, orgRole, currentOrg, isError };
}
